import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useLocationWeather, {
  LOCATION_CACHE_KEY,
} from "../hooks/useLocationWeather";
import useWeatherForecast from "../hooks/useWeatherForecast";
import usePlantProtectionUnifiedStats from "../hooks/usePlantProtectionUnifiedStats";
import usePlatformViewTenant from '../hooks/usePlatformViewTenant';
import PlatformStatsBreakdownModal from '../components/PlatformStatsBreakdownModal';
import { isPlatformAdmin } from '../utils/permissions';
import { loadPlatformBannerStats } from '../utils/platformBannerStats';
import AlertModal from '../components/AlertModal';
import { getWelcomeOverview } from '../api';
import {
  loadWelcomeCriticalParallel,
  loadWelcomeSilentParallel,
  scheduleWelcomeSilent,
} from '../api/welcomeBootstrap';
import { loadWelcomeOperationSlots } from '../api/welcomeShellData';
import { loadHomeFarmPreview } from '../api/homeFarmPreview';
import { isLoggedIn } from '../utils/token.js';
import { useUser } from '../store/UserContext';
import { resolvePrimaryFarmWeatherLocation } from '../utils/primaryFarmWeatherLocation';
import { navigateToInquiry } from '../utils/inquiryEntryContext';
import { buildWeatherAgronomyPath } from '../utils/weatherAgronomyContext';
import { mergeBusinessContextSearch, readBusinessContext } from '../utils/businessContextNav';

const DEFAULT_OVERVIEW_LOCATION = {
  province: '北京市',
  city: '北京市',
  district: '朝阳区',
  latitude: 39.9042,
  longitude: 116.4074,
};

function readCachedOverviewLocation(cacheKey) {
  try {
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    const coords = cached?.coords || {};
    const addr = cached?.address || {};
    if (coords.latitude == null || coords.longitude == null) return null;
    return {
      province: addr.province || DEFAULT_OVERVIEW_LOCATION.province,
      city: addr.city || DEFAULT_OVERVIEW_LOCATION.city,
      district: addr.district || DEFAULT_OVERVIEW_LOCATION.district,
      latitude: coords.latitude,
      longitude: coords.longitude,
    };
  } catch {
    return null;
  }
}

import {
  WelcomeBanner,
  HomeOperationSection,
  AIInquirySection,
  WeatherChart,
  PestWarningSection,
  PlantProtectionAdvice,
  MyFarmSection,
  FarmOverviewCard,
  CropDetailsModal,
} from './welcome-page/components';

import { DeviceStatusCard, SoilMoistureCard, TodayTasksCard, FarmCalendarCard } from './welcome-page/components/DashboardCards';

export default function WelcomePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useUser();
  const { kpis: unifiedKpis } = usePlantProtectionUnifiedStats('farm');
  const platformAdmin = isPlatformAdmin();
  const { tenants, setViewTenantId } = usePlatformViewTenant();
  const [platformBannerStats, setPlatformBannerStats] = useState(null);
  const [platformBannerLoading, setPlatformBannerLoading] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [breakdownMetric, setBreakdownMetric] = useState(null);
  const {
    location,
    address,
    weather,
    loading,
    permissionDenied,
    status,
  } = useLocationWeather();

  const [farmWeatherLoc, setFarmWeatherLoc] = useState(null);

  /** 实时 GPS（非默认点）；定位中也可用缓存/主农场降级，避免长期转圈 */
  const hasRealtimeLocation =
    location?.latitude != null &&
    location?.longitude != null &&
    !location?.isDefault &&
    location?.source !== 'default' &&
    location?.source !== 'cache' &&
    location?.source !== 'ip' &&
    status !== 'locating' &&
    !loading;

  const hasCachedLocation =
    location?.latitude != null &&
    location?.longitude != null &&
    location?.source === 'cache';

  const farmCoordsReady =
    farmWeatherLoc?.latitude != null && farmWeatherLoc?.longitude != null;

  /** 无实时点时用主农场坐标（定位失败 / 权限拒绝 / 仍在定位） */
  const needFarmFallback =
    farmCoordsReady &&
    !hasRealtimeLocation &&
    !hasCachedLocation &&
    (
      loading ||
      status === 'locating' ||
      permissionDenied ||
      status === 'denied' ||
      status === 'failed' ||
      location?.source === 'default' ||
      location?.latitude == null ||
      location?.longitude == null
    );

  const currentLatitude = hasRealtimeLocation
    ? location.latitude
    : hasCachedLocation
      ? location.latitude
      : needFarmFallback
        ? farmWeatherLoc.latitude
        : location?.latitude ?? farmWeatherLoc?.latitude;
  const currentLongitude = hasRealtimeLocation
    ? location.longitude
    : hasCachedLocation
      ? location.longitude
      : needFarmFallback
        ? farmWeatherLoc.longitude
        : location?.longitude ?? farmWeatherLoc?.longitude;

  const weatherLatitude = currentLatitude;
  const weatherLongitude = currentLongitude;

  const { forecast } = useWeatherForecast(weatherLatitude, weatherLongitude);

  const [aiInput, setAIInput] = useState('');
  const [aiLoading, setAILoading] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [alertModal, setAlertModal] = useState({ isOpen: false, message: '', title: '提示' });
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [welcomeData, setWelcomeData] = useState(null);
  const [welcomeLoading, setWelcomeLoading] = useState(true);
  const [farmPreview, setFarmPreview] = useState(null);
  const [farmPreviewLoading, setFarmPreviewLoading] = useState(true);
  const [weatherChartExpanded, setWeatherChartExpanded] = useState(true);
  /** 运营位 / 设备：欢迎页只拉一次，下发给各卡片 */
  const [opSlots, setOpSlots] = useState({ carousel: [], topic: [] });
  const [opSlotsLoading, setOpSlotsLoading] = useState(true);
  const [shellDevices, setShellDevices] = useState({
    devices: [],
    iotDevices: [],
    devicesOnline: 0,
  });
  /** 次要：静默填充，不阻塞首屏 spinner */
  const [shellDevicesLoading, setShellDevicesLoading] = useState(false);

  const WELCOME_OVERVIEW_CACHE_KEY = 'plantai_welcome_overview_cache_v2';
  const WELCOME_OVERVIEW_CACHE_TTL = 2 * 60 * 1000;

  const currentLocationInfo = useMemo(() => {
    if (needFarmFallback && farmWeatherLoc) {
      return {
        province: farmWeatherLoc.province || address?.province || '-',
        city: farmWeatherLoc.city || address?.city || farmWeatherLoc.province || '-',
        district: farmWeatherLoc.district || address?.district || '-',
        latitude: farmWeatherLoc.latitude,
        longitude: farmWeatherLoc.longitude,
      };
    }
    return {
      province: address?.province || '-',
      city: address?.city && address.city !== '[]' ? address.city : address?.province || '-',
      district: address?.district || '-',
      latitude: location?.latitude,
      longitude: location?.longitude
    };
  }, [address, location, needFarmFallback, farmWeatherLoc]);

  const overviewLocationInfo = useMemo(() => {
    const lat = currentLocationInfo.latitude ?? location?.latitude;
    const lng = currentLocationInfo.longitude ?? location?.longitude;
    if (lat != null && lng != null) {
      return { ...currentLocationInfo, latitude: lat, longitude: lng };
    }
    return readCachedOverviewLocation(LOCATION_CACHE_KEY) || DEFAULT_OVERVIEW_LOCATION;
  }, [currentLocationInfo, location]);

  const locationRequestKey = useMemo(() => {
    const loc = overviewLocationInfo;
    const lat = loc.latitude != null ? Number(loc.latitude).toFixed(4) : '';
    const lng = loc.longitude != null ? Number(loc.longitude).toFixed(4) : '';
    return `${loc.province}|${loc.city}|${loc.district}|${lat}|${lng}`;
  }, [overviewLocationInfo]);

  const loadWelcomeData = useCallback((options = {}) => {
    const force = options === true || options?.force === true;
    const silent = options?.silent === true;
    if (!locationRequestKey) {
      setWelcomeLoading(false);
      return;
    }

    if (!force) {
      try {
        const raw = localStorage.getItem(WELCOME_OVERVIEW_CACHE_KEY);
        if (raw) {
          const cache = JSON.parse(raw);
          if (cache?.key === locationRequestKey && Date.now() - (cache.timestamp || 0) < WELCOME_OVERVIEW_CACHE_TTL) {
            setWelcomeData(cache.data);
            setWelcomeLoading(false);
            return;
          }
        }
      } catch {}
    } else {
      try {
        localStorage.removeItem(WELCOME_OVERVIEW_CACHE_KEY);
      } catch {}
    }

    const loc = overviewLocationInfo;
    // 关键首包要 loading；定位微调的静默刷新不打断 UI
    if (!silent) setWelcomeLoading(true);
    getWelcomeOverview({
      latitude: loc.latitude,
      longitude: loc.longitude,
      province: loc.province,
      city: loc.city,
      district: loc.district
    })
      .then(res => {
        if (res.data) {
          setWelcomeData(res.data);
          try {
            localStorage.setItem(WELCOME_OVERVIEW_CACHE_KEY, JSON.stringify({
              key: locationRequestKey, data: res.data, timestamp: Date.now()
            }));
          } catch {}
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!silent) setWelcomeLoading(false);
      });
  }, [locationRequestKey, overviewLocationInfo]);

  // 首屏关键：立刻用缓存/默认坐标并发拉取概览（不等 GPS）
  useEffect(() => {
    loadWelcomeData({ force: false, silent: false });
    // 仅 mount + key 首帧；后续定位精炼走静默
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 定位稳定后静默刷新概览（防抖，不抢首屏）
  useEffect(() => {
    if (!locationRequestKey) return undefined;
    const delayMs = loading ? 800 : 280;
    const timer = setTimeout(() => {
      loadWelcomeData({ force: false, silent: true });
    }, delayMs);
    return () => clearTimeout(timer);
  }, [loadWelcomeData, loading, locationRequestKey]);

  /**
   * 首屏关键并行：运营位 + 农场壳层
   * 静默并行：农场 KPI + 设备/IoT（让路首屏连接）
   */
  useEffect(() => {
    let cancelled = false;
    let cancelSilent = () => {};
    const ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const loggedIn = isLoggedIn();

    setOpSlotsLoading(true);
    setFarmPreviewLoading(true);

    loadWelcomeCriticalParallel({
      loggedIn,
      useJwtTenant: platformAdmin,
      devicesOnline: 0,
      signal: ac?.signal,
    })
      .then(({ slots, shellCtx }) => {
        if (cancelled) return;
        setOpSlots(slots || { carousel: [], topic: [] });
        setOpSlotsLoading(false);
        if (shellCtx?.preview) {
          setFarmPreview(shellCtx.preview);
        }
        setFarmPreviewLoading(false);

        cancelSilent = scheduleWelcomeSilent(() => {
          if (cancelled) return;
          loadWelcomeSilentParallel(shellCtx, {
            useJwtTenant: platformAdmin,
            devicesOnline: 0,
            signal: ac?.signal,
          }).then(({ farmFull, devices }) => {
            if (cancelled) return;
            if (farmFull) setFarmPreview(farmFull);
            if (devices) setShellDevices(devices);
          });
        }, { delayMs: 80, idleTimeoutMs: 900 });
      })
      .catch(() => {
        if (cancelled) return;
        setOpSlots({ carousel: [], topic: [] });
        setOpSlotsLoading(false);
        setFarmPreviewLoading(false);
        // 关键失败仍静默拉设备，避免整页永久空
        cancelSilent = scheduleWelcomeSilent(() => {
          if (cancelled) return;
          loadWelcomeSilentParallel(null, {
            useJwtTenant: platformAdmin,
            devicesOnline: 0,
            signal: ac?.signal,
          }).then(({ devices }) => {
            if (!cancelled && devices) setShellDevices(devices);
          });
        }, { delayMs: 120, idleTimeoutMs: 1200 });
      });

    const reloadSlots = () => {
      loadWelcomeOperationSlots()
        .then((data) => {
          if (!cancelled) setOpSlots(data);
        })
        .catch(() => {
          if (!cancelled) setOpSlots({ carousel: [], topic: [] });
        });
    };
    const onStorage = (e) => {
      if (e.key === 'home-operation-slots-rev') reloadSlots();
    };
    window.addEventListener('home-operation-slots-changed', reloadSlots);
    window.addEventListener('storage', onStorage);

    return () => {
      cancelled = true;
      ac?.abort();
      cancelSilent();
      window.removeEventListener('home-operation-slots-changed', reloadSlots);
      window.removeEventListener('storage', onStorage);
    };
  }, [user?.id, platformAdmin]);

  // 设备在线数单点补丁，不重复拉农场合集接口
  useEffect(() => {
    setFarmPreview((prev) => {
      if (!prev?.counts) return prev;
      if (prev.counts.devicesOnline === shellDevices.devicesOnline) return prev;
      return {
        ...prev,
        counts: { ...prev.counts, devicesOnline: shellDevices.devicesOnline },
      };
    });
  }, [shellDevices.devicesOnline]);

  const primaryFarmId = farmPreview?.hero && !farmPreview.hero.isDemo ? farmPreview.hero.farmId : '';
  const primaryFarmName = farmPreview?.hero && !farmPreview.hero.isDemo ? (farmPreview.hero.name || '') : '';
  const primaryFarmLocation = farmPreview?.hero && !farmPreview.hero.isDemo ? (farmPreview.hero.location || '') : '';

  // 从已绑定地块的设备推导默认 landId（不自动写 deviceId，避免误绑）
  const derivedLandId = useMemo(() => {
    const farm = String(primaryFarmId || '').trim();
    const list = Array.isArray(shellDevices?.devices) ? shellDevices.devices : [];
    for (const d of list) {
      const lid = d?.landId != null ? String(d.landId).trim() : '';
      if (!lid) continue;
      if (farm) {
        const df = String(d?.farmId || d?.farm_id || '').trim();
        if (df && df !== farm) continue;
      }
      return lid;
    }
    return '';
  }, [primaryFarmId, shellDevices?.devices]);

  // 欢迎页 URL 同步 farmId/landId；保留已有 deviceId；加载中不改
  useEffect(() => {
    if (farmPreviewLoading) return;
    const id = String(primaryFarmId || '').trim();
    const currentFarm = String(searchParams.get('farmId') || '').trim();
    const currentLand = String(searchParams.get('landId') || '').trim();
    const currentDevice = String(searchParams.get('deviceId') || '').trim();

    if (!id) {
      if (!currentFarm && !currentLand && !currentDevice) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('farmId');
          next.delete('landId');
          next.delete('deviceId');
          return next;
        },
        { replace: true }
      );
      return;
    }

    const patch = {};
    if (currentFarm !== id) {
      patch.farmId = id;
      // 换农场：先清旧地块/设备，避免串口径
      patch.landId = null;
      patch.deviceId = null;
    }
    const landKeep = currentFarm === id ? currentLand : '';
    if (!landKeep && derivedLandId) patch.landId = derivedLandId;
    if (Object.keys(patch).length === 0) return;

    setSearchParams(
      (prev) => new URLSearchParams(mergeBusinessContextSearch(prev, patch)),
      { replace: true }
    );
  }, [primaryFarmId, derivedLandId, farmPreviewLoading, searchParams, setSearchParams]);

  const bizCtx = useMemo(() => readBusinessContext(searchParams), [searchParams]);

  const farmFallbackLabel = useMemo(() => {
    if (!needFarmFallback || !farmWeatherLoc) return '';
    const city = farmWeatherLoc.city || farmWeatherLoc.province || '';
    const district = farmWeatherLoc.district ? ` · ${farmWeatherLoc.district}` : '';
    if (city) return `${city}${district}`;
    if (primaryFarmName) return primaryFarmName;
    return '主农场位置';
  }, [needFarmFallback, farmWeatherLoc, primaryFarmName]);

  useEffect(() => {
    let cancelled = false;
    if (!primaryFarmId) {
      setFarmWeatherLoc(null);
      return undefined;
    }
    resolvePrimaryFarmWeatherLocation({ farmId: primaryFarmId, location: primaryFarmLocation })
      .then((loc) => {
        if (!cancelled) setFarmWeatherLoc(loc);
      })
      .catch(() => {
        if (!cancelled) setFarmWeatherLoc(null);
      });
    return () => { cancelled = true; };
  }, [primaryFarmId, primaryFarmLocation]);

  const reloadFarmPreview = useCallback(async () => {
    const loggedIn = isLoggedIn();
    setFarmPreviewLoading(true);
    try {
      const data = await loadHomeFarmPreview(loggedIn, {
        useJwtTenant: platformAdmin,
        devicesOnline: shellDevices.devicesOnline,
      });
      setFarmPreview(data);
    } catch {
      setFarmPreview(null);
    } finally {
      setFarmPreviewLoading(false);
    }
  }, [platformAdmin, shellDevices.devicesOnline]);

  // 平台管理员横幅统计：静默填充（不与首屏抢并发）
  useEffect(() => {
    if (!platformAdmin || !tenants.length) {
      setPlatformBannerStats(null);
      return undefined;
    }
    let cancelled = false;
    const cancelSilent = scheduleWelcomeSilent(() => {
      if (cancelled) return;
      setPlatformBannerLoading(true);
      loadPlatformBannerStats(tenants)
        .then((data) => {
          if (!cancelled) setPlatformBannerStats(data);
        })
        .catch(() => {
          if (!cancelled) setPlatformBannerStats(null);
        })
        .finally(() => {
          if (!cancelled) setPlatformBannerLoading(false);
        });
    }, { delayMs: 400, idleTimeoutMs: 2000 });
    return () => {
      cancelled = true;
      cancelSilent();
    };
  }, [platformAdmin, tenants]);

  // 硬超时：不阻塞首屏 UI
  useEffect(() => {
    if (!welcomeLoading) return undefined;
    const t = window.setTimeout(() => setWelcomeLoading(false), 12000);
    return () => window.clearTimeout(t);
  }, [welcomeLoading]);

  useEffect(() => {
    if (!opSlotsLoading) return undefined;
    const t = window.setTimeout(() => {
      setOpSlotsLoading(false);
      setOpSlots((prev) => prev || { carousel: [], topic: [] });
    }, 10000);
    return () => window.clearTimeout(t);
  }, [opSlotsLoading]);

  useEffect(() => {
    if (!farmPreviewLoading) return undefined;
    const t = window.setTimeout(() => setFarmPreviewLoading(false), 12000);
    return () => window.clearTimeout(t);
  }, [farmPreviewLoading]);

  const plantProtectionAdvice = welcomeData?.plantingAdvice || {};
  const pestWarnings = plantProtectionAdvice.pestWarnings || [];
  const weatherInfo = welcomeData?.weather || {};
  const bannerWeather = useMemo(() => {
    const overview = weatherInfo && typeof weatherInfo === 'object' ? weatherInfo : {};
    const live = weather && typeof weather === 'object' ? weather : {};
    const overviewUsable =
      Object.keys(overview).length > 0 &&
      !overview.error &&
      (overview.temperature != null || overview.temp != null || overview.humidity != null);
    const merged = overviewUsable ? { ...live, ...overview } : { ...live };
    const humidityRaw =
      merged.humidity ?? merged.humidityAvg ?? merged.humidity_avg ?? merged.rh ?? null;
    const humidityNum = humidityRaw == null || humidityRaw === '' ? null : Number(humidityRaw);
    return {
      ...merged,
      temp: merged.temp ?? merged.temperature ?? null,
      tempMax: merged.tempMax ?? merged.temp_max ?? null,
      tempMin: merged.tempMin ?? merged.temp_min ?? null,
      text: merged.text || merged.description || merged.condition || '',
      windDir: merged.windDir || merged.windDirection || merged.wind_dir || '',
      windScale:
        merged.windScale ??
        merged.windLevel ??
        merged.wind_scale ??
        merged.windSpeed ??
        merged.wind_speed ??
        null,
      humidity: Number.isFinite(humidityNum) ? humidityNum : null,
    };
  }, [weatherInfo, weather]);

  const weekdayNames = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
  const now = new Date();
  const currentDateStr = `${now.getFullYear()}年${now.getMonth() + 1}月${now.getDate()}日 ${weekdayNames[now.getDay()]}`;

  /** 欢迎页进问询默认不带地块，由用户在问询页手动绑定 */
  const sendAIMessage = async () => {
    if (!aiInput.trim() && selectedImages.length === 0) {
      setAlertModal({ isOpen: true, message: '请先输入问题或上传图片', title: '提示' });
      return;
    }
    setAILoading(true);
    try {
      navigateToInquiry(navigate, {
        text: aiInput.trim(),
        images: selectedImages,
        imagePreviewUrls,
      });
    } finally {
      // 跳转后本页可能卸载；若未跳转也要复位
      setTimeout(() => setAILoading(false), 800);
    }
  };

  const handleImageUpload = (event) => {
    const input = event.target;
    const picked = Array.from(input?.files || []);
    // 允许同文件再次选择
    if (input) input.value = '';

    const imageFiles = picked.filter(
      (f) => f && (String(f.type || '').startsWith('image/') || /\.(jpe?g|png|webp|gif|bmp)$/i.test(f.name || ''))
    );
    if (imageFiles.length === 0) {
      setAlertModal({
        isOpen: true,
        message: '请选择图片文件（JPG/PNG/WEBP 等）',
        title: '提示'
      });
      return;
    }
    if (imageFiles.length + selectedImages.length > 5) {
      setAlertModal({ isOpen: true, message: '最多只能上传5张图片', title: '提示' });
      return;
    }

    imageFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e?.target?.result;
        if (typeof dataUrl === 'string') {
          setImagePreviewUrls((prev) => [...prev, dataUrl]);
        }
      };
      reader.readAsDataURL(file);
    });
    setSelectedImages((prev) => [...prev, ...imageFiles]);
  };

  const handleRemoveImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePaste = (event) => {
    const items = event.clipboardData?.items;
    if (!items) return;
    const imageFiles = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) imageFiles.push(file);
      }
    }
    if (imageFiles.length === 0) return;
    if (imageFiles.length + selectedImages.length > 5) {
      setAlertModal({ isOpen: true, message: '最多只能上传5张图片', title: '提示' });
      return;
    }

    event.preventDefault();
    imageFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e?.target?.result;
        if (typeof dataUrl === 'string') {
          setImagePreviewUrls((prev) => [...prev, dataUrl]);
        }
      };
      reader.readAsDataURL(file);
    });
    setSelectedImages((prev) => [...prev, ...imageFiles]);
    // 粘贴后自动进入问询（与验收「欢迎页带图进问询」一致）；默认不自动绑定地块
    setTimeout(() => {
      navigateToInquiry(navigate, {
        pastedImages: imageFiles,
        imagePreviewUrls: [],
      });
    }, 300);
  };

  const displayUserName = user?.realName || user?.nickname || user?.username || user?.name || '';

  const overviewStats = useMemo(() => {
    if (platformAdmin) {
      return (
        platformBannerStats?.totals ?? {
          farms: 0,
          plots: 0,
          devices: 0,
          warnings: 0,
          activeJobs: 0,
          crops: 0
        }
      );
    }
    const counts = farmPreview?.counts || {};
    return {
      farms: Number(counts.farmCount) || 0,
      plots: Number(counts.plotTotal) || 0,
      devices: Number(counts.devicesOnline) || 0,
      warnings: Number(counts.warnN) || pestWarnings.length || 0,
      activeJobs: Number(unifiedKpis.inProgressOperations) || 0,
      crops: Number(unifiedKpis.activeCrops) || 0,
    };
  }, [platformAdmin, platformBannerStats, farmPreview, pestWarnings, unifiedKpis]);

  const handleBannerStatClick = useCallback((metricKey) => {
    if (!platformAdmin) return;
    setBreakdownMetric(metricKey);
    setBreakdownOpen(true);
  }, [platformAdmin]);

  const handleSelectTenantFromBreakdown = useCallback(
    (tenantId) => {
      setViewTenantId(tenantId);
      setBreakdownOpen(false);
      navigate('/production/land');
    },
    [setViewTenantId, navigate]
  );

  return (
    <div className="w-full min-h-full bg-gray-50 p-4 lg:p-6">
      {/* 一、横幅区域 */}
      <WelcomeBanner
        userName={displayUserName}
        weather={bannerWeather}
        currentDateStr={currentDateStr}
        address={needFarmFallback ? {
          province: farmWeatherLoc?.province || address?.province,
          city: farmWeatherLoc?.city || address?.city,
          district: farmWeatherLoc?.district || address?.district,
        } : address}
        farmFallbackLabel={farmFallbackLabel}
        onViewWeatherTrend={() => setWeatherChartExpanded(true)}
        onOpenWeatherPage={() => {
          navigate(
            buildWeatherAgronomyPath('/weather-agronomy/weather-data', {
              farmId: primaryFarmId || bizCtx.farmId || '',
              landId: bizCtx.landId || '',
              lat: currentLatitude ?? '',
              lng: currentLongitude ?? '',
              address: needFarmFallback
                ? farmFallbackLabel
                : [address?.city, address?.district].filter(Boolean).join(' ') || '',
            })
          );
        }}
        overviewStats={overviewStats}
        statsClickable={platformAdmin}
        statsLoading={platformAdmin && platformBannerLoading && !platformBannerStats}
        statsHint={platformAdmin ? '（全平台合计，点击数字查看各租户明细）' : ''}
        onStatClick={handleBannerStatClick}
      />

      <PlatformStatsBreakdownModal
        isOpen={breakdownOpen}
        onClose={() => setBreakdownOpen(false)}
        highlightKey={breakdownMetric}
        byTenant={platformBannerStats?.byTenant || []}
        loading={platformBannerLoading}
        onSelectTenant={handleSelectTenantFromBreakdown}
      />

      {/* 二、预警提醒条 */}
      <PestWarningSection
        pestWarnings={pestWarnings}
        loading={welcomeLoading}
        farmId={primaryFarmId || bizCtx.farmId || ''}
        landId={bizCtx.landId || ''}
        deviceId={bizCtx.deviceId || ''}
      />

      {/* 二点五、运营位专题（轮播由农场总览消费，不再重复请求） */}
      <div className="mb-8">
        <HomeOperationSection topics={opSlots.topic} loading={opSlotsLoading} />
      </div>

      {/* 三、三栏主体布局：农场总览 | 我的农场 | 植保建议+AI助手 */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8 items-start">
        <div className="lg:col-span-4">
          <FarmOverviewCard
            farmPreview={farmPreview}
            loading={farmPreviewLoading}
            carouselSlots={opSlots.carousel}
            opLoading={opSlotsLoading}
            smartDevices={shellDevices.devices}
            devicesLoading={shellDevicesLoading}
            landId={bizCtx.landId || ''}
          />
        </div>
        <div className="lg:col-span-4">
          <MyFarmSection
            farmPreview={farmPreview}
            loading={farmPreviewLoading}
            onPrimaryFarmChange={reloadFarmPreview}
            farmId={primaryFarmId}
            landId={bizCtx.landId || ''}
            deviceId={bizCtx.deviceId || ''}
          />
        </div>
        <div className="lg:col-span-4 flex flex-col gap-6 min-w-0">
          <PlantProtectionAdvice
            welcomeLoading={welcomeLoading}
            plantProtectionAdvice={plantProtectionAdvice}
            farmId={primaryFarmId}
            onRefresh={() => loadWelcomeData({ force: true })}
          />
          <AIInquirySection
            aiInput={aiInput}
            setAIInput={setAIInput}
            aiLoading={aiLoading}
            onSendMessage={sendAIMessage}
            onImageUpload={handleImageUpload}
            onPaste={handlePaste}
            imagePreviewUrls={imagePreviewUrls}
            onRemoveImage={handleRemoveImage}
          />
        </div>
      </div>

      {/* 四、气象趋势 + 右侧辅助卡片 */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
        <div className="lg:col-span-7">
          <WeatherChart
            weather={bannerWeather}
            forecast={forecast}
            loading={false}
          />
        </div>
        <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <DeviceStatusCard
            devices={shellDevices.devices}
            iotDevices={shellDevices.iotDevices}
            loading={shellDevicesLoading}
            farmId={primaryFarmId || bizCtx.farmId || ''}
            landId={bizCtx.landId || ''}
            deviceId={bizCtx.deviceId || ''}
          />
          <SoilMoistureCard
            devices={shellDevices.devices}
            loading={shellDevicesLoading}
            farmId={primaryFarmId || bizCtx.farmId || ''}
            landId={bizCtx.landId || ''}
            deviceId={bizCtx.deviceId || ''}
          />
          <TodayTasksCard deferMs={360} />
          <FarmCalendarCard
            farmId={primaryFarmId || bizCtx.farmId || ''}
            landId={bizCtx.landId || ''}
            deviceId={bizCtx.deviceId || ''}
          />
        </div>
      </div>

      {/* 弹窗 */}
      <CropDetailsModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        selectedCrop={selectedCrop}
        getCropDetails={() => ({})}
      />
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        title={alertModal.title}
        confirmText={alertModal.confirmText || '确定'}
        onConfirm={alertModal.onConfirm}
        onClose={() => setAlertModal({ isOpen: false, message: '', title: '提示' })}
      />
    </div>
  );
}
